/**
 * Design tokens — SINGLE SOURCE OF TRUTH.
 *
 * Mirror of: stitch_ripik.ai_industrial_autopilot_saas 2/ripik_core/DESIGN.md
 *
 * To change tokens app-wide: edit here. Both MUI theme (muiTheme.js) and all
 * components consume this file via useThemeColors().
 */

// ── Brand palette ──────────────────────────────────────────────────────────────
const brand = {
  navyDeep:   '#202475',
  navy:       '#1e4396',
  navyLight:  '#2a7ab5',
  accent:     '#3EB5E7',
  primary:    '#002c78',
};

// ── Light mode surfaces ────────────────────────────────────────────────────────
const light = {
  textStrong:     '#334155',
  // Backgrounds
  bg:             '#ffffff',
  bgPage:         '#f0f3f8',
  bgSubtle:       '#f8fafc',
  bgMuted:        '#f1f5f9',
  bgHover:        'rgba(30,67,150,0.04)',
  bgToggleHover:  'rgba(30,67,150,0.06)',
  bgActiveGrad:   'linear-gradient(135deg, rgba(32,36,117,0.07), rgba(62,181,231,0.06))',

  // Borders
  border:         'rgba(30,67,150,0.08)',
  borderSubtle:   '#e2e8f0',
  borderInput:    '#c4c6d3',
  borderActive:   'rgba(62,181,231,0.15)',
  divider:        '#f1f5f9',

  // Text
  textPrimary:    '#0F172A',
  textSecondary:  '#191c1e',
  textMuted:      '#64748B',
  textSubtle:     '#94a3b8',
  textLabel:      '#444651',
  textActive:     '#1e4396',
  textPlaceholder:'#747683',

  // Topbar
  topbarBg:       '#ffffff',
  topbarShadow:   '0 1px 0 rgba(30,67,150,0.06)',
  dropdownBg:     '#ffffff',
  dropdownShadow: '0 8px 32px rgba(30,67,150,0.12)',
  toggleBg:       '#f1f5f9',
  toggleActive:   'rgba(32,36,117,0.08)',
  toggleActiveText:'#1e4396',
  toggleInactive: '#64748B',
};

// ── Dark mode surfaces ─────────────────────────────────────────────────────────
const dark = {
  textStrong:     '#cbd5e1',
  bgShimmer:      '#252d40',
  // Backgrounds
  bg:             '#141927',
  bgPage:         '#0f1219',
  bgSubtle:       '#1a2030',
  bgMuted:        '#1e2536',
  bgHover:        'rgba(255,255,255,0.05)',
  bgToggleHover:  'rgba(255,255,255,0.07)',
  bgActiveGrad:   'linear-gradient(135deg, rgba(32,36,117,0.3), rgba(62,181,231,0.15))',

  // Borders
  border:         'rgba(255,255,255,0.07)',
  borderSubtle:   'rgba(255,255,255,0.08)',
  borderInput:    'rgba(255,255,255,0.12)',
  borderActive:   'rgba(62,181,231,0.25)',
  divider:        'rgba(255,255,255,0.06)',

  // Text
  textPrimary:    '#e2e8f0',
  textSecondary:  '#e2e8f0',
  textMuted:      '#b0bec5',
  textSubtle:     '#a0aec0',
  textLabel:      '#b0bec5',
  textActive:     '#3EB5E7',
  textPlaceholder:'#a0aec0',

  // Topbar
  topbarBg:       '#1a1f2e',
  topbarShadow:   '0 1px 0 rgba(255,255,255,0.05)',
  dropdownBg:     '#1e2536',
  dropdownShadow: '0 8px 32px rgba(0,0,0,0.4)',
  toggleBg:       'rgba(255,255,255,0.08)',
  toggleActive:   'rgba(62,181,231,0.18)',
  toggleActiveText:'#1e4396',
  toggleInactive: '#b0bec5',
};

// ── Semantic / state colors (mode-independent) ─────────────────────────────────
const semantic = {
  error:          '#ba1a1a',
  errorBg:        '#ffdad6',
  errorContainer: 'rgba(186,26,26,0.06)',
  errorBorder:    'rgba(186,26,26,0.18)',
  errorAlt:       '#ef4444',
  warning:        '#d97706',
  warningSolid:   '#f59e0b',
  warningBg:      'rgba(217,119,6,0.08)',
  warningBorder:  'rgba(245,158,11,0.25)',
  success:        '#059669',
  successBg:      'rgba(16,185,129,0.08)',
  successBorder:  'rgba(16,185,129,0.2)',
  successAlt:     '#10b981',
  info:           '#1e4396',
  infoBg:         'rgba(30,67,150,0.08)',
  infoBorder:     'rgba(30,67,150,0.15)',
  infoCyan:       '#0891b2',
  infoCyanBg:     'rgba(8,145,178,0.08)',
  infoCyanBorder: 'rgba(8,145,178,0.2)',
  infoCyanBorderStrong: 'rgba(8,145,178,0.28)',
  infoCyanBorderMid: 'rgba(8,145,178,0.25)',
  violet:         '#7c3aed',
  violetBorder:   'rgba(124,58,237,0.25)',
  successGreen:   '#16a34a',
  successGreenBg: 'rgba(34,197,94,0.08)',
  successGreenBorder: 'rgba(34,197,94,0.2)',
  blue:           '#2563EB',
  blueBg:         'rgba(37,99,235,0.1)',
  errorBorderSoft: 'rgba(220,38,38,0.3)',
  brandBorder:    'rgba(30,67,150,0.3)',
  brandBorderSoft: 'rgba(30,67,150,0.2)',
  brandBg:        'rgba(30,67,150,0.1)',
  brandBorderAccent: 'rgba(62,181,231,0.15)',
  brandBorderAccentMid: 'rgba(62,181,231,0.25)',
  warningBorderStrong: 'rgba(245,158,11,0.35)',
  overlay:        'rgba(0,0,0,0.45)',
  overlayMid:     'rgba(0,0,0,0.4)',
  overlaySoft:    'rgba(0,0,0,0.25)',

  // Brand colors (use these instead of hardcoding hex)
  brandNavy:      '#202475',
  brandNavyMid:   '#1e4396',
  brandNavyLight: '#2a7ab5',
  brandAccent:    '#3EB5E7',

  // Active indicator gradient (sidebar)
  activeIndicator: 'linear-gradient(180deg, #202475, #3EB5E7)',
  // Brand gradient (topbar accent, card bar)
  brandGrad:       'linear-gradient(90deg, #202475 0%, #1e4396 40%, #3EB5E7 100%)',
  // Primary button gradient
  btnPrimaryGrad:  'linear-gradient(90deg, #202475 0%, #1e4396 50%, #2a7ab5 100%)',
  // Logo mark gradient
  logoGrad:        'linear-gradient(135deg, #202475 0%, #1e4396 60%, #3EB5E7 100%)',
};

// ── Parameter/user type badge color maps ──────────────────────────────────────
// Centralised here so LayersTab, ParametersTab, UsersTab all reference one source
const paramTypeMeta = {
  target:       { bg: 'rgba(30,67,150,0.08)',   border: 'rgba(30,67,150,0.2)',   text: '#1e4396', label: 'Target',       bar: 'linear-gradient(180deg,#202475,#3EB5E7)' },
  controllable: { bg: 'rgba(16,185,129,0.08)',  border: 'rgba(16,185,129,0.2)',  text: '#059669', label: 'Controllable', bar: 'linear-gradient(180deg,#059669,#34d399)' },
  observed:       { bg: 'rgba(245,158,11,0.08)',  border: 'rgba(245,158,11,0.2)',  text: '#d97706', label: 'Observed',      bar: 'linear-gradient(180deg,#d97706,#fbbf24)' },
  monitored:      { bg: 'rgba(139,92,246,0.08)',  border: 'rgba(139,92,246,0.2)',  text: '#7c3aed', label: 'Monitored',     bar: 'linear-gradient(180deg,#7c3aed,#a78bfa)' },
  unclassified:   { bg: 'rgba(100,116,139,0.08)', border: 'rgba(100,116,139,0.15)',text: '#64748b', label: 'Unclassified',  bar: 'linear-gradient(180deg,#64748b,#94a3b8)' },
};

const userRoleMeta = {
  ripik_admin:  { bg: 'rgba(30,67,150,0.08)',   border: 'rgba(30,67,150,0.2)',   text: '#1e4396', label: 'Ripik Admin' },
  ripik_user:   { bg: 'rgba(16,185,129,0.08)',  border: 'rgba(16,185,129,0.2)',  text: '#059669', label: 'Ripik User' },
  client_admin: { bg: 'rgba(245,158,11,0.08)',  border: 'rgba(245,158,11,0.2)',  text: '#d97706', label: 'Admin' },
  client_user:  { bg: 'rgba(100,116,139,0.08)', border: 'rgba(100,116,139,0.15)',text: '#64748B', label: 'User' },
};

// Layer color palette for color pickers
const layerColors = ['#1e4396', '#059669', '#d97706', '#7c3aed', '#db2777', '#0891b2'];

// ── Status badge colors (WCAG AAA compliant) ───────────────────────────────────
const status = {
  active:       { bg: '#d1fae5', border: '#6ee7b7', text: '#064e3b' },
  provisioning: { bg: '#fef3c7', border: '#fcd34d', text: '#78350f' },
  inactive:     { bg: '#f1f5f9', border: '#cbd5e1', text: '#1e293b' },
  archived:     { bg: '#fee2e2', border: '#fca5a5', text: '#7f1d1d' },
};

// ── Typography ─────────────────────────────────────────────────────────────────
const typography = {
  fontFamily: {
    heading: '"Hanken Grotesk", system-ui, sans-serif',
    body:    '"Inter", system-ui, sans-serif',
    mono:    'ui-monospace, SFMono-Regular, Menlo, monospace',
  },
  fontSize: {
    xs:   '10px',
    sm:   '11px',
    base: '12px',
    md:   '13px',
    lg:   '14px',
    xl:   '15px',
    '2xl':'16px',
    '3xl':'18px',
    '4xl':'20px',
    '5xl':'22px',
    '6xl':'26px',   // display — large stat/hero numbers
    '7xl':'28px',   // display — OTP digits, biggest hero numbers
  },
  fontWeight: {
    normal:   400,
    medium:   500,
    semibold: 600,
    bold:     700,
    extrabold:800,
  },
  letterSpacing: {
    tight:   '-0.02em',
    normal:  '0',
    wide:    '0.02em',
    wider:   '0.04em',
    label:   '0.05em',
    widest:  '0.06em',
    otp:     '0.5em',   // spaced-out single-digit OTP input
  },
};

// ── Border radius ──────────────────────────────────────────────────────────────
const radius = {
  xs:   '4px',
  sm:   '6px',
  md:   '8px',
  lg:   '10px',
  xl:   '12px',
  '2xl':'14px',
  '3xl':'16px',
  full: '9999px',
};

// ── Spacing ────────────────────────────────────────────────────────────────────
const spacing = {
  containerMax:   '1440px',
  gutter:         '24px',
  marginMobile:   '16px',
  marginDesktop:  '32px',
  stackXs:        '4px',
  stackSm:        '8px',
  stackMd:        '16px',
  stackLg:        '24px',
  stackXl:        '32px',
};

// ── Z-index ────────────────────────────────────────────────────────────────────
const zIndex = {
  sidebar:  200,
  topbar:   300,
  dropdown: 400,
  modal:    500,
  toast:    600,
};

// ── Shadow ─────────────────────────────────────────────────────────────────────
const shadow = {
  industrial: '0px 4px 12px rgba(30, 67, 150, 0.08)',
  card:       '0 4px 6px rgba(30,67,150,0.04), 0 12px 40px rgba(30,67,150,0.10)',
  cardFull:   '0 4px 6px rgba(30,67,150,0.04), 0 12px 40px rgba(30,67,150,0.10), 0 1px 2px rgba(30,67,150,0.06)',
  btnPrimary: '0 2px 8px rgba(32,36,117,0.30), 0 1px 2px rgba(32,36,117,0.20)',
  btnSm:      '0 2px 8px rgba(32,36,117,0.25)',
  topbar:     '0 2px 6px rgba(30,67,150,0.22)',
  drawer:     '-8px 0 32px rgba(0,0,0,0.12)',
  panel:      '0 2px 6px rgba(30,67,150,0.05)',
  panelSoft:  '0 2px 6px rgba(30,67,150,0.04)',
  search:     '0 1px 4px rgba(30,67,150,0.05)',
  toast:      '0 4px 16px rgba(0,0,0,0.10)',
};

// ── Named exports for backward compat + tree-shaking ──────────────────────────
export { brand, light, dark, semantic, status, paramTypeMeta, userRoleMeta, layerColors, typography, radius, spacing, zIndex, shadow };

// ── Legacy flat export (muiTheme.js depends on this shape) ────────────────────
export const tokens = {
  colors: {
    // Surfaces
    surface:                  light.bgPage,
    surfaceDim:               '#d8dadc',
    surfaceBright:            light.bgPage,
    surfaceContainerLowest:   light.bg,
    surfaceContainerLow:      '#f2f4f6',
    surfaceContainer:         '#eceef0',
    surfaceContainerHigh:     '#e6e8ea',
    surfaceContainerHighest:  '#e0e3e5',
    onSurface:                '#191c1e',
    onSurfaceVariant:         '#444651',
    inverseSurface:           '#2d3133',
    inverseOnSurface:         '#eff1f3',
    outline:                  '#747683',
    outlineVariant:           '#c4c6d3',

    // Primary
    primary:            brand.primary,
    onPrimary:          '#ffffff',
    primaryContainer:   brand.navy,
    onPrimaryContainer: '#9cb5ff',
    primaryFixed:       '#dbe1ff',
    inversePrimary:     '#b3c5ff',
    surfaceTint:        '#395aae',

    // Secondary
    secondary:          '#006687',
    onSecondary:        '#ffffff',
    secondaryContainer: '#59cafd',
    onSecondaryContainer:'#00536f',

    // Tertiary
    tertiary:          '#572100',
    onTertiary:        '#ffffff',
    tertiaryContainer: '#7b3200',

    // Error
    error:          semantic.error,
    onError:        '#ffffff',
    errorContainer: semantic.errorBg,
    onErrorContainer:'#93000a',

    // Brand accents
    navyDeep:  brand.navyDeep,
    skyBright: '#70CDE7',
    slateText: light.textPrimary,
    slateMuted: light.textMuted,

    background:   light.bgPage,
    onBackground: '#191c1e',
  },

  typography,
  radius,
  spacing,
  shadow,
};
